import { useMarca } from '@/components/providers/MarcaProvider';
import { Formik, FormikProps } from 'formik';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FichaService, SeguradoraService, UsuarioService } from '../../services';
import { firstDayOfMonth, formatDate, lastDayOfMonth } from '@/utils/DateUtils';
import { Seguro } from '@/types/Seguro';
import { getMesLabel } from '@/types/enum/Mes';
import { getLinkFichasDoMes } from '@/utils/FichaUtils';
import { PageRequest } from '@/types/pagination';
import { Sort } from '@/types/sort';
import { Card } from '@/components/ui/card';
import { ActionButton, Button } from '@/components/ui/button';
import { Badge, IconeTile } from '@/components/ui/cores';
import { PageHeader } from '@/components/ui/page-header';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import * as pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { CalendarClock, ChevronLeft, ChevronRight, EyeIcon, FileIcon } from 'lucide-react';
import { getTenant } from '@/utils/TenantUtils';
import { AxiosError } from 'axios';
import { ErrorPage } from '../ErrorPage';
import { FormikAutoComplete } from '@/components/ui/autocomplete';
import { Seguradora } from '@/types/SeguradoraDTO';
import { Usuario } from '@/types/usuario';

type FichaTotal = {
  mes: string;
  quantidade: number;
  expirados: number;
}

const RelatorioSementesAnoPage: React.FC = () => {
  const marca = useMarca();
  const {ano} = useParams();
  const [fichas, setFichas] = useState<FichaTotal[]>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AxiosError>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [seguradora, setSeguradora] = useState<Seguradora>();
  const [corretor, setCorretor] = useState<Usuario>();
  

  useEffect(() => {
    setLoading(true);

    setSeguradora(undefined);
    setCorretor(undefined);
    if (searchParams.get('seguradora') !== null && searchParams.get('seguradora')){
      SeguradoraService.getByCodigo(searchParams.get('seguradora')!)
        .then(result => setSeguradora(result.data));
    }

    if (searchParams.get('corretor') !== null && searchParams.get('corretor')){
      UsuarioService.getByCodigo(searchParams.get('corretor')!)
        .then(result => setCorretor(result.data));
    }

    FichaService.findFichasPorAno(Number(ano), searchParams.get('seguradora')!, searchParams.get('corretor')!)
      .then(response => setFichas(response.data))
      .catch(setError)
      .finally(() => setLoading(false));
  },[ano, searchParams]);

  const onSubmit = (values: any) => {
    navigate(`/${getTenant()}/relatorios/seguros-vencendo/${values.ano}?seguradora=${values.seguradora ? values.seguradora.id : ''}&corretor=${values.corretor ? values.corretor.id : ''}`);
  }

  const onSelect = async (item: FichaTotal, ano: number) => {
    setLoading(true);
    
    const firstDay = firstDayOfMonth(Number(item.mes)-1, ano);
    const lastDay = lastDayOfMonth(Number(item.mes)-1, ano);   
    
    const filtroSeguradora = seguradora ? `;seguradora.nome=='${seguradora?.nome}'` : '';
    const filtroCorretor = corretor ? `;corretor.nome=='${corretor?.nome}'` : '';
    
    let page: PageRequest = {
      page: 0,
      size: 9999999
    };

    let sort: Sort = {
      sort: 'vigenciaFinal,asc'
    };

    try{
      let itens = await FichaService.findFichas(`vigenciaFinal=ge="${formatDate(firstDay)}";vigenciaFinal=le="${formatDate(lastDay)}"${filtroSeguradora}${filtroCorretor}`,page, sort)
      gerarRelatorio(itens.data.content, Number(item.mes), ano);
    }catch (error: any){
      console.error({ title: 'Erro ao gerar o relatório' }, error);
    }finally{
      setLoading(false) 
    }    
  }

  const getComissaoLiquidaFicha = (premioLiquido: number, porcentagemComissao: number) => {
    return (premioLiquido*porcentagemComissao)/100;
  }

  const gerarRelatorio = (itens: Seguro[], mes: number, ano: number) => {
    let USDollar = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL', 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 3 
    });

    const dateFormat = new Intl.DateTimeFormat('pt-BR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });

    let dados = itens.map(item => [
      (item.segurado?.nome.length! > 23 ? {text: item.segurado?.nome} : {text: item.segurado?.nome, style: 'tableCellMargin'}), 
      (item.seguradora?.nome.length! > 23 ? {text: item.seguradora?.nome} : {text: item.seguradora?.nome, style: 'tableCellMargin'}), 
      {text: item.placa, style: 'tableCellMargin'}, 
      {text: dateFormat.format(new Date(`${item.vigenciaInicial}T00:00:00`))+' - '+dateFormat.format(new Date(`${item.vigenciaFinal}T00:00:00`)), style: 'tableCellMargin'}, 
      {text: USDollar.format(item.premioLiquido!), style: 'tableCellMargin'}, 
      {text: `${USDollar.format(getComissaoLiquidaFicha(item.premioLiquido!, item.percentualComissao!))} (${item.percentualComissao}%)`, style: 'tableCellMargin'}]);

    dados.push([
        {text: '', style: 'tableCellMargin'}, 
        {text: '', style: 'tableCellMargin'}, 
        {text: '', style: 'tableCellMargin'}, 
        {text: '', style: 'tableCellMargin'}, 
        {text: '', style: 'tableCellMargin'}, 
        {text: '', style: 'tableCellMargin'}]);  

    dados.splice(0, 0, [
      {text: 'Segurado', style: 'tableCellHeader'}, 
      {text: 'Seguradora', style: 'tableCellHeader'}, 
      {text: 'Placa', style: 'tableCellHeader'}, 
      {text: 'Vigência', style: 'tableCellHeader'}, 
      {text: 'Prêmio Líquido', style: 'tableCellHeader'}, 
      {text: 'Comissão Líquido', style: 'tableCellHeader'}]);

    var dd = {
      pageSize: 'A4',
      pageOrientation: 'portrait',
      pageMargins: [ 20, 20, 20, 20 ],
      // header: 'simple text',
      footer: (currentPage: any, pageCount: any) => { 
        return  { text: "Página " + currentPage.toString() + " de " + pageCount, style: 'footer', alignment: 'center'}
      },
      content: [
        {
          text: `${marca.titulo}\n`,
          style: 'header',
          alignment: 'center',
          tocItem: true
        },
        {
          text: `Seguros Vencendo em ${getMesLabel(mes.toString())} de ${ano}\n`,
          style: 'subheader1',
          alignment: 'center',
          tocItem: true
        },
        {
          text: `Seguradora: ${seguradora?.nome || 'TODOS'}\n`,
          style: 'subheader2',
          alignment: 'center',
          tocItem: true
        },
        {
          text: `Corretor: ${corretor?.nome || 'TODOS'}\n`,
          style: 'subheader2',
          alignment: 'center',
          tocItem: true
        },
        {
          style: 'tableExample',
          table: {
            widths: [100, 100, 40, 92, 70, 70],
            heights: 20,
            body: dados
          },
          layout: 'lightHorizontalLines',
          tocItem: true
        }
      ],
      styles: {
        header: {
          fontSize: 14,
          bold: true,
          alignment: 'justify'
        },
        subheader1: {
          fontSize: 14,
          bold: false,
          alignment: 'justify'
        },
        subheader2: {
          fontSize: 10,
          bold: false,
          alignment: 'justify'
        },
        footer: {
          fontSize: 8,
          bold: false
        },
        tableCellMargin: {
          marginTop: 5
        },
        tableCellHeader: {
          marginTop: 10,
          bold: true
        },
        tableExample: {
          margin: [0, 5, 0, 15],
          fontSize: 8,
        }
      }
    };

    // pdfMake.vfs = pdfFonts.pdfMake.vfs;
    (pdfMake as any).vfs = pdfFonts.pdfMake.vfs;
    (pdfMake as any).createPdf(dd).open();
  }

  if (error){
    return <ErrorPage axiosError={error} />
  }

  const irPara = (novoAno: number) => navigate(`/${getTenant()}/relatorios/seguros-vencendo/${novoAno}?${searchParams.toString()}`);
  const total = (campo: 'quantidade' | 'expirados') => (fichas || []).reduce((t, item) => t + Number(item[campo] || 0), 0);

  return (
    <div className="space-y-6">
      <PageHeader icon={<IconeTile cor="bg-amber-500/10 text-amber-600 dark:text-amber-400"><CalendarClock /></IconeTile>} title="Seguros vencendo">
        <div className="flex items-center rounded-md border">
          <Button variant="ghost" size="icon" aria-label="Ano anterior" onClick={() => irPara(Number(ano) - 1)}><ChevronLeft className="h-4 w-4" /></Button>
          <span className="w-14 text-center text-sm font-medium tabular-nums">{ano}</span>
          <Button variant="ghost" size="icon" aria-label="Próximo ano" onClick={() => irPara(Number(ano) + 1)}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </PageHeader>

      <Formik initialValues={{ano: ano, seguradora: seguradora, corretor: corretor}} onSubmit={onSubmit} enableReinitialize>
        {(formikProps: FormikProps<any>) => (
          <div className="grid gap-4 sm:grid-cols-2 lg:max-w-3xl">
            <FormikAutoComplete
                onSearch={SeguradoraService.autoCompleteAtivo}
                name="seguradora"
                placeholder="Todas as seguradoras"
                getOptionLabel={item => `${item.nome}`}
                getOptionValue={item => item}
                getKey={item => item.id}
                onValueChange={() => formikProps.handleSubmit()}
              />
            <FormikAutoComplete
                onSearch={UsuarioService.autoCompleteAtivo}
                name="corretor"
                placeholder="Todos os corretores"
                getOptionLabel={item => item.nome && `${item.nome}`}
                getOptionValue={item => item}
                getKey={item => item.id}
                onValueChange={() => formikProps.handleSubmit()}
              />
          </div>
        )}
      </Formik>

      <Card className="overflow-hidden shadow-none">
        <Table>
          <TableHeader className="bg-muted/40">
            <TableRow className="hover:bg-transparent">
              <TableHead className="h-10 px-4 text-xs">Mês</TableHead>
              <TableHead className="h-10 px-4 text-right text-xs">Vencendo</TableHead>
              <TableHead className="h-10 px-4 text-right text-xs">Vencidos sem renovação</TableHead>
              <TableHead className="h-10 w-24 px-4"><span className="sr-only">Ações</span></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {fichas?.length === 0 && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={4} className="h-32 text-center text-sm text-muted-foreground">Nenhum seguro vence em {ano}.</TableCell>
              </TableRow>
            )}
            {fichas?.map(item => (
              <TableRow key={item.mes} className="group">
                <TableCell className="px-4 font-medium">{getMesLabel(item.mes)}</TableCell>
                <TableCell className="px-4 text-right tabular-nums">{item.quantidade}</TableCell>
                <TableCell className="px-4 text-right tabular-nums">
                  {Number(item.expirados) > 0
                    ? <Badge className="bg-rose-500/10 text-rose-700 dark:text-rose-300">{item.expirados}</Badge>
                    : <span className="text-muted-foreground/60">—</span>}
                </TableCell>
                <TableCell className="px-4">
                  <div className="flex justify-end gap-1">
                    <ActionButton icon={<EyeIcon />} label="Ver seguros" onClick={() => navigate(getLinkFichasDoMes(Number(item.mes), Number(ano), seguradora, corretor))} />
                    <ActionButton icon={<FileIcon />} label="Relatório PDF" onClick={() => onSelect(item, Number(ano))} />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          {!!fichas?.length && (
            <TableFooter>
              <TableRow className="font-semibold">
                <TableCell className="px-4">Total do ano</TableCell>
                <TableCell className="px-4 text-right tabular-nums">{total('quantidade')}</TableCell>
                <TableCell className="px-4 text-right tabular-nums">{total('expirados')}</TableCell>
                <TableCell />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </Card>
    </div>
  )
}

export default RelatorioSementesAnoPage;